import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { FilePicker } from "@capawesome/capacitor-file-picker";
import {
    IonAccordion,
    IonAccordionGroup,
    IonButton,
    IonCheckbox,
    IonContent,
    IonDatetime,
    IonDatetimeButton,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonModal,
    IonPage,
    IonProgressBar,
    IonTextarea,
    IonToolbar,
    isPlatform,
    useIonToast
} from "@ionic/react";
import { CapacitorVideoCompression, CompressionProgressEvent, VideoQuality } from "capacitor-video-compression-plugin";
import { alertCircleOutline, chevronDownOutline, informationCircleOutline } from "ionicons/icons";
import React, { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player/lazy";
import { useHistory, useLocation } from "react-router";
import { v4 as uuidv4 } from 'uuid';
import { Events, Params, Screens, useAnalytics } from "../AppAnalyticsProvider";
import { useAppState } from "../AppListenerProvider";
import { AppContext } from "../AppStateProvider";
import { useAuth } from "../AuthProvider";
import CustomHelmet from "../components/ComponentsFunctional/CustomHelmet/CustomHelmet";
import Dropdown from "../components/ComponentsUI/Dropdown/Dropdown";
import IDXButtonIcon from "../components/ComponentsUI/IDXButtonIcon/IDXButtonIcon";
import EventHashtagSelect from "../components/ComponentsUI/EventHashtagSelect/EventHashtagSelect";
import MultiSelectHashtag from "../components/ComponentsUI/MultiSelectHashtag/MultiSelectHashtag";
import TagSelectOnUpload from "../components/ComponentsUI/TagSelectOnUpload/TagSelectOnUpload";
import { ClipType } from "../models/clipType";
import { FilmerInfo } from "../models/filmerInto";
import { LoadSuggestedTricksStatus } from "../models/loadSuggestedTricksStatus";
import { LocationInput } from "../models/locationInput";
import { SelectOptions } from "../models/profileSelectOptions";
import { SportType } from "../models/sportType";
import { User } from "../models/user";
import { UserProfileSearch } from "../models/user-profile-search";
import { UserTrick } from "../models/user-trick";
import LocationService from "../services/location.service";
import PreferencesService from "../services/preferences.service";
import SearchService from "../services/search.service";
import { default as UserService, default as userService } from "../services/user.service";
import { base64FromBlob, FOLLOWERS_ONLY, userUploadsDirectory } from "../services/utils";
import { usePhoneVerification } from "../PhoneVerificationProvider";
import { gateWebUploadTap } from "./uploadGate";
import LoadingPage from "./LoadingPage";
import LoginButtonPage from "./LoginButtonPage";

const sportOptions: SelectOptions[] = [
    {
        id: 1,
        name: SportType.SKIING,
    },
    {
        id: 2,
        name: SportType.SNOWBOARDING,
    }
];

const features: SelectOptions[] = [
    {
        id: 1,
        name: 'Jump',
    },
    {
        id: 2,
        name: 'Rail/Jib',
    },
    {
        id: 3,
        name: 'Halfpipe',
    },
    {
        id: 3,
        name: 'Freeride',
    },
    {
        id: 4,
        name: 'Off-Snow',
    },
];

const options = [
    { value: ClipType.CLIP, label: 'Clip' },
    { value: ClipType.LINE, label: 'Line' },
    { value: ClipType.EDIT, label: 'Edit' },
];

const otherFeatures: SelectOptions[] = [];

const trickNamePlaceholderText = [
    'switch lip 270, dub kinkinator slide, crowd pleaser, frontside 540...',
    'frontside 360, the streets arent safe, back swap transfer, butternut squash...'
]

const processingVerbs = [
    // Classic
    'Analyzing',
    'Processing',
    'Loading',

    // Casual/Fun
    'Vibing with',
    'Checking out',
    'Scoping',
    'Peeping',
    'Eyeballing',
    'Sizing up',

    // Cooking themed
    'Cooking up',
    'Brewing',
    'Marinating',
    'Simmering',
    'Baking',
    'Toasting',
    'Grilling',
    'Seasoning',

    // Thinking themed
    'Contemplating',
    'Pondering',
    'Mulling over',
    'Meditating on',

    // Action themed
    'Crunching',
    'Munching on',
    'Devouring',
    'Absorbing',
    'Digesting',

    // Technical-ish
    'Decoding',
    'Deciphering',
    'Unpacking',
    'Unraveling',

    // Ski/Snow park culture (on brand!)
    'Shredding',
    'Sending',
    'Stomping',
    'Buttering',
    'Jibbing',
    'Boosting',
    'Afterbanging',
    'Grabbing',
    'Tweaking',
    'Boning out',
    'Steezing',
    'Styling on',
    'Corking',
    'Pressing',
    'Bonking',
    'Tapping',
    'Hucking',
    'Lapping',
    'Sessioning',
    'Ripping through',
    'Slaying',
    'Mobbing',
    'Floating',
    'Popping',
    'Dropping into',
    'Locking into',
    'Sliding through',
    'Zeaching',
    'Safety-ing',
    'Muting',
    'Tail-grabbing',
    'Method-ing',
    'Japan-ing',
    'Stacking',

    // Misc fun
    'Perusing',
    'Savoring',
    'Admiring',
    'Appreciating',
    'Hyping up',
    'Booling on',
    'Stunting on',
    'Levitating',
    'Raging on',
    'Innovating',
];

const Upload: React.FC = () => {
    const { isNative, platform } = useAppState();
    const { state, dispatch } = React.useContext(AppContext);
    const history = useHistory();
    const locationReact = useLocation();
    const { trackEvent, trackScreen } = useAnalytics();
    const { requirePhone, hasVerifiedPhone } = usePhoneVerification();

    const [selectedFile, setSelectedFile] = useState();
    const [desiredBlobName, setDesiredBlobName] = useState<string>(undefined);

    const [trickName, setTrickName] = useState("");
    const [trickCaption, setTrickCaption] = useState("");
    const [hashtags, setHashtags] = useState([]);

    const [location, setLocation] = useState("");
    const [locationInput, setLocationInput] = useState<LocationInput>(null);
    const [locationDropdownOptions, setLocationDropdownOptions] = useState<LocationInput[]>([]);
    const [locationDropdown, setLocationDropdown] = useState(false);

    const [filmerInfo, setFilmerInfo] = useState<FilmerInfo | null>(null);
    const [filmerSearchText, setFilmerSearchText] = useState("");
    const [filmerDropdownOptions, setFilmerDropdownOptions] = useState<UserProfileSearch[]>([]);
    const [filmerDropdown, setFilmerDropdown] = useState(false);

    const [sport, setSport] = useState("");
    const [feature, setFeature] = useState("");
    const [preview, setPreview] = useState("");

    const [linkLat, setLinkLat] = useState(undefined);
    const [linkLng, setLinkLng] = useState(undefined);
    const [linkCreationTime, setLinkCreationTime] = useState(undefined);
    const [linkAlt, setLinkAlt] = useState(undefined);

    const [selectedTags, setSelectedTags] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [filmDate, setFilmDate] = useState(new Date(new Date().toDateString()).toISOString());
    const [videoUploading, setVideoUploading] = useState(false);
    const [videoProcessing, setVideoProcessing] = useState(false);
    const [compressionProgress, setCompressionProgress] = useState<number>(0);
    const [compressionPhase, setCompressionPhase] = useState<'compressing' | 'uploading'>('compressing');

    const [processingVerb, setProcessingVerb] = useState(() =>
        processingVerbs[Math.floor(Math.random() * processingVerbs.length)]
    );

    const [present] = useIonToast();

    const [trickUploaderTrickNamePlaceholderText, setTrickUploaderTrickNamePlaceholderText] = useState(trickNamePlaceholderText[0]);

    // states for determining what to show to users
    const [fileLoaded, setFileLoaded] = useState<boolean>(false);
    const [fileIsLink, setFileIsLink] = useState<boolean>(false);
    const [fileLink, setFileLink] = useState<string>(undefined);
    const [showTrickForm, setShowTrickForm] = useState(false);
    const [showTagSelect, setShowTagSelect] = useState(false);

    // Edit
    const [editLink, setEditLink] = useState<string>("");
    const [isValidLink, setIsValidLink] = useState<boolean>(false);

    const { user, isLoading, isAuthenticated } = useAuth();
    const [userProfile, setProfile] = useState(null);
    const [profileError, setProfileError] = useState(false);
    const [isAuthed, setIsAuthed] = useState(undefined);

    const previewVideoRef = useRef(null);

    const [displayFilmerInfoEntry, setDisplayFilmerInfoEntry] = useState(false);
    const [displayClipInfoEntry, setDisplayClipInfoEntry] = useState(false);
    const [displayTrickInfoEntry, setDisplayTrickInfoEntry] = useState(false);

    const CHUNK_SIZE = 5 * 1024 * 1024
    const MAX_FILE_SIZE = 512000 // 500 MB
    const MAX_FILE_SIZE_ANDROID_PLAY = 20480 // 20MB
    const MAX_FILE_SIZE_IPHONE_PLAY = 32768 // 32MB
    const MAX_FILE_SIZE_WEB_PLAY = 32768 // 32MB

    const [unlisted, setUnlisted] = useState(false);

    const [googlePlacesSessionToken, setGooglePlacesSessionToken] = useState(null);

    // Control Flow
    const [segmentValue, setSegmentValue] = useState<string>(ClipType.CLIP);

    // Upload Start Time
    const [uploadStartTime, setUploadStartTime] = useState<number>(0);

    /**
     * Tracks the furthest-reached stage of the upload flow. Used to fire
     * `upload_abandoned` with the correct `upload_stage` param if the user
     * leaves the page before completing. Must be a ref (not state) so the
     * unmount cleanup reads the latest value, not the value at mount time.
     *
     * Stages (monotonic progression):
     *   idle          -> initial mount, no file yet
     *   file_selected -> user picked a video
     *   compressing   -> client-side compression in progress
     *   uploading     -> file bytes being sent to the backend
     *   completed     -> upload succeeded; skip abandonment event on unmount
     */
    const uploadStageRef = useRef<'idle' | 'file_selected' | 'compressing' | 'uploading' | 'completed'>('idle');
    const uploadEnteredAtRef = useRef<number>(0);

    // Track initial screen view
    useEffect(() => {
        trackScreen(Screens.UPLOAD, 'UploadScreen');
        trackEvent('upload_screen_viewed', {
            is_authenticated: isAuthenticated,
            platform: platform,
            is_native: isNative
        });
        uploadEnteredAtRef.current = Date.now();

        // Fire `upload_abandoned` if the user leaves without completing.
        // Powers the upload funnel panel on the analytics dashboard.
        return () => {
            if (uploadStageRef.current !== 'completed' && uploadStageRef.current !== 'idle') {
                const durationSeconds = Math.round((Date.now() - uploadEnteredAtRef.current) / 1000);
                trackEvent(Events.UPLOAD_ABANDONED, {
                    [Params.UPLOAD_STAGE]: uploadStageRef.current,
                    [Params.DURATION_SECONDS]: durationSeconds,
                    [Params.PLATFORM]: platform,
                });
            }
        };
    }, []);

    useEffect(() => {
        console.log("On the upload page and rendering");
        setProfileError(false);
        setProfile(null);

        // Location
        setLocationDropdown(false);
        setLocationDropdownOptions([]);
        setLocationInput(null);
        setLocation("");
        setGooglePlacesSessionToken(uuidv4());

        const random = Math.floor(Math.random() * trickNamePlaceholderText.length);
        setTrickUploaderTrickNamePlaceholderText(trickNamePlaceholderText[random]);

        const getUserProfile = async () => {
            console.log("getting user profile")
            const response: User = await UserService.getUserByAuthId(user?.uid);
            if (response === null) {
                setProfileError(true);
            }

            setProfile(response);

            if (response !== null && response !== undefined) {
                if (response.profile.sports.length === 1) {
                    setSport(response.profile.sports[0]);
                }
            }
        }

        if (!isLoading) {
            window.prerenderReady = true;
            if (isAuthenticated) {
                getUserProfile();
            }
        }
    }, [isLoading, user?.uid, isAuthenticated]);

    useEffect(() => {
        console.log("On the upload page and rendering");

        setDesiredBlobName(null);
    }, []);

    useEffect(() => {
        if (videoProcessing || videoUploading) {
            // Set initial random verb
            const initialVerbIndex = Math.floor(Math.random() * processingVerbs.length);
            setProcessingVerb(processingVerbs[initialVerbIndex]);

            // Rotate fun verbs every 2 seconds
            const verbIntervalId = setInterval(() => {
                const randomVerbIndex = Math.floor(Math.random() * processingVerbs.length);
                setProcessingVerb(processingVerbs[randomVerbIndex]);
            }, 2000);

            return () => clearInterval(verbIntervalId);
        }
    }, [videoProcessing, videoUploading]);

    const storeVideoiOS = async (blob: any, pathName: string) => {
        const base64Data = await base64FromBlob(blob);
        const savedFile = await Filesystem.writeFile({
            path: pathName,
            data: base64Data,
            directory: Directory.Cache,
            recursive: true
        });
        return savedFile.uri;
    }

    const storeVideo = async (blob: any, pathName: string) => {
        if (Capacitor.getPlatform() === "ios") {
            return await storeVideoiOS(blob, pathName);
        } else if (Capacitor.getPlatform() === "android") {
            console.log("Storing in the file path");
            // can assume all videos will be uploaded under 25MB which should cause android to not crash
            return await storeVideoiOS(blob, pathName);
        } else {
            return await storeVideoiOS(blob, pathName);
        }
    }

    const fetchVideoAndPlay = (videoRef: any, url: string) => {
        const video = videoRef.current;
        if (!video) return;

        // Set the source directly
        video.src = url;
        video.load();

        // Wait for enough data to show thumbnail
        video.onloadeddata = () => {
            console.log("Video data loaded");
            // Seek to 0.1s to force frame render for thumbnail
            video.currentTime = 0.1;
        };

        video.onseeked = () => {
            console.log("Video seeked, thumbnail should be visible");
        };

        video.oncanplay = () => {
            video.play().catch((e: Error) => console.log('Autoplay blocked:', e));
            video.muted = false;
            console.log("Video has started playing");
        };

        video.onerror = (e: any) => {
            console.log("Video error:", e);
            // Try fetching as blob as fallback
            fetch(url)
                .then(response => response.blob())
                .then(blob => {
                    const blobUrl = URL.createObjectURL(blob);
                    video.src = blobUrl;
                    video.load();
                })
                .catch(err => console.log("Blob fetch also failed:", err));
        };
    }

    const processVideoWeb = async (objectUrl: string) => {
        try {
            // Validate the object URL
            if (!objectUrl.startsWith('blob:')) {
                console.error('Invalid object URL format:', objectUrl);
                return;
            }

            // Create a new blob from the object URL to ensure proper format
            try {
                const response = await fetch(objectUrl);
                const blob = await response.blob();

                // Log blob details for debugging
                console.log('Video blob details:', {
                    type: blob.type,
                    size: blob.size
                });

                // Create a new blob URL
                const newBlobUrl = URL.createObjectURL(blob);
                setPreview(newBlobUrl);

                // Clean up the old URL
                URL.revokeObjectURL(objectUrl);

                if (previewVideoRef.current) {
                    // Reset video element
                    previewVideoRef.current.currentTime = 0;

                    // For Safari, we need to set the source directly
                    previewVideoRef.current.src = newBlobUrl;

                    // Wait for metadata to load before playing
                    previewVideoRef.current.onloadedmetadata = async () => {
                        try {
                            previewVideoRef.current!.muted = !isNative;
                            await previewVideoRef.current!.play();
                        } catch (playError) {
                            console.error('Error playing video:', playError);
                        }
                    };

                    // Force load
                    await previewVideoRef.current.load();
                }
            } catch (error) {
                console.error('Error processing blob:', error);
            }
        } catch (error) {
            console.error('Error in processVideoWeb:', error);
        }
    };

    const processVideoMobile = async (blob: any, pathName: string) => {
        const mobileVideoUri = await storeVideo(blob, pathName);
        console.log("URL Storage: " + mobileVideoUri);
        const objectUrlCapacitor = Capacitor.convertFileSrc(mobileVideoUri)
        console.log("URL Preview: " + objectUrlCapacitor);
        setPreview(objectUrlCapacitor);
        if (Capacitor.getPlatform() === 'android') {
            // @ts-ignore
            if (previewVideoRef.current) {
                previewVideoRef.current.load();
                fetchVideoAndPlay(previewVideoRef, objectUrlCapacitor);
            }
        } else {
            if (previewVideoRef.current) {
                previewVideoRef.current.play();
                previewVideoRef.current.muted = !isNative;
            }
        }
    }

    const getFileExtensionFromBlob = (blob: any) => {
        let filename = blob.name || '';
        let dotIndex = filename.lastIndexOf('.');
        let extension = dotIndex >= 0 ? filename.substring(dotIndex + 1).toLowerCase() : '';
        return extension || 'mp4';
    }

    /**
     * Determines optimal compression quality based on video duration.
     * Uses HEVC (H.265) encoding for ~50% smaller files than H.264.
     *
     * Expected file sizes with HEVC (approximate):
     * - 30 sec medium (720p): ~12 MB
     * - 60 sec medium (720p): ~24 MB
     * - 90 sec medium (720p): ~35 MB
     * - 120 sec low (540p): ~30 MB
     * - 180 sec low (540p): ~45 MB
     */
    const getSmartCompressionQuality = (durationSeconds: number): VideoQuality => {
        if (durationSeconds <= 90) {
            return 'medium'; // 720p HEVC - Instagram/TikTok quality
        } else {
            return 'low'; // 540p HEVC - longer videos need smaller resolution
        }
    }

    /**
     * Client-side video compression using native iOS/Android APIs.
     * Compresses video locally before upload for much faster upload times.
     */
    const compressVideoNative = async (filePath: string): Promise<{ success: boolean; compressedPath?: string; error?: string }> => {
        try {
            console.log('Starting native compression for:', filePath);

            // Get video info to determine smart quality
            const videoInfo = await CapacitorVideoCompression.getVideoInfo({ source: filePath });
            console.log('Video info:', JSON.stringify(videoInfo));

            const quality = getSmartCompressionQuality(videoInfo.duration);

            trackEvent('upload_compression_native_started', {
                original_size: videoInfo.size,
                duration: videoInfo.duration,
                width: videoInfo.width,
                height: videoInfo.height,
                quality_selected: quality,
                platform: platform
            });

            console.log(`Compressing video: ${videoInfo.duration}s, ${videoInfo.width}x${videoInfo.height}, quality: ${quality}`);

            // Listen for compression progress
            const progressListener = await CapacitorVideoCompression.addListener(
                'compressionProgress',
                (event: CompressionProgressEvent) => {
                    setCompressionProgress(event.progress);
                    if (Math.round(event.progress * 100) % 10 === 0) {
                        console.log(`Compression progress: ${Math.round(event.progress * 100)}%`);
                    }
                }
            );

            // Compress the video
            console.log('Calling compressVideo...');
            const result = await CapacitorVideoCompression.compressVideo({
                source: filePath,
                quality: quality,
                includeAudio: true,
            });

            console.log('Compression result:', JSON.stringify(result));

            // Remove the progress listener
            await progressListener.remove();

            trackEvent('upload_compression_native_completed', {
                original_size: result.originalSize,
                compressed_size: result.size,
                compression_ratio: result.compressionRatio,
                compression_time_ms: result.compressionTimeMs,
                final_width: result.width,
                final_height: result.height,
                quality_used: quality,
                platform: platform
            });

            console.log(`Compression complete: ${result.originalSize} -> ${result.size} bytes (${result.compressionRatio.toFixed(2)}x reduction)`);
            console.log('Compressed file path:', result.path);

            return { success: true, compressedPath: result.path };

        } catch (error) {
            console.error('Native compression failed:', error);
            trackEvent('upload_compression_native_failed', {
                error: error instanceof Error ? error.message : 'Unknown error',
                platform: platform
            });
            return { success: false, error: error instanceof Error ? error.message : 'Compression failed' };
        }
    }

    /**
     * Server-side compression fallback for web platform.
     * Used when native compression is not available.
     */
    const compressVideoServer = async (blob: any) => {
        const fileExtension = getFileExtensionFromBlob(blob);
        const filename = uuidv4().toString()

        console.log("Uploading to server for processing");
        setCompressionPhase('uploading');
        setCompressionProgress(0); // Reset progress for upload phase

        const CHUNK_SIZE = 12 * 1024 * 1024; // 12 MB
        const fileSize = blob.size;
        const chunks = Math.ceil(fileSize / CHUNK_SIZE);
        const infd_id = userProfile.id;

        for (let i = 0; i < chunks; i++) {
            const start = i * CHUNK_SIZE;
            const end = Math.min(fileSize, start + CHUNK_SIZE);
            const chunk = blob.slice(start, end);

            const formData = new FormData();
            formData.append('file', chunk, `${filename}.part${i}`);
            formData.append('filename', filename);

            const chunkRes = await UserService.uploadTrickInChunks(formData, infd_id, filename);
            console.log("Chunk uploaded: " + chunkRes);
            setCompressionProgress((i + 1) / chunks);
        }

        console.log("Assembling on server");
        const urlObject = await UserService.uploadUserTrickAssemble(infd_id, filename, fileExtension);

        return urlObject;
    }

    const compressVideoForViewing = async (blob: any, filePath?: string) => {
        trackEvent('upload_compression_processing', {
            file_size: blob ? blob.size / 1024 : 0,
            file_type: blob ? blob.type : 'native_path',
            is_native: isNative,
            has_native_path: !!filePath
        });
        // Ensure processing UI is showing (may already be set from onSelectFile for faster feedback)
        setVideoProcessing(true);
        setCompressionProgress(0);
        setCompressionPhase('compressing');

        // Use native compression on iOS/Android for much faster processing
        if (isNative && filePath && (platform === 'ios' || platform === 'android')) {
            try {
                const compressionResult = await compressVideoNative(filePath);

                if (compressionResult.success && compressionResult.compressedPath) {
                    console.log('Native compression successful, path:', compressionResult.compressedPath);

                    // The path from compression is already a file:// URL
                    let compressedFilePath = compressionResult.compressedPath;

                    // Convert to capacitor-compatible URL for preview
                    const previewUrl = Capacitor.convertFileSrc(compressedFilePath.replace('file://', ''));
                    console.log('Preview URL:', previewUrl);

                    try {
                        // Read compressed file as blob for upload using XMLHttpRequest
                        // (fetch sometimes fails with CORS on capacitor:// URLs)
                        const compressedBlob = await new Promise<Blob>((resolve, reject) => {
                            const xhr = new XMLHttpRequest();
                            xhr.open('GET', previewUrl, true);
                            xhr.responseType = 'blob';
                            xhr.onload = () => {
                                if (xhr.status === 200 || xhr.status === 0) {
                                    resolve(xhr.response);
                                } else {
                                    reject(new Error(`XHR failed with status: ${xhr.status}`));
                                }
                            };
                            xhr.onerror = () => reject(new Error('XHR network error'));
                            xhr.send();
                        });

                        const compressedSizeKB = compressedBlob.size / 1024;
                        console.log('Compressed blob size:', compressedBlob.size, 'bytes (', compressedSizeKB, 'KB)');

                        // Check if compressed file is small enough for direct upload
                        // If still too large, need to use server-side chunked upload
                        const maxDirectUploadKB = platform === 'ios' ? MAX_FILE_SIZE_IPHONE_PLAY : MAX_FILE_SIZE_ANDROID_PLAY;

                        if (compressedSizeKB > maxDirectUploadKB) {
                            console.log(`Compressed file still too large (${compressedSizeKB}KB > ${maxDirectUploadKB}KB), using server chunked upload`);
                            trackEvent('upload_compression_native_still_large', {
                                compressed_size: compressedSizeKB,
                                max_size: maxDirectUploadKB,
                                platform: platform
                            });

                            // Use server-side chunked upload for the compressed file
                            const urlObject = await compressVideoServer(compressedBlob);

                            if (urlObject && urlObject['url']) {
                                setFileIsLink(true);
                                setFileLink(urlObject['url']);
                                setPreview(urlObject['url']);

                                if ('lat' in urlObject && urlObject['lat'] !== null) {
                                    setLinkLat(urlObject['lat'].toString());
                                }
                                if ('long' in urlObject && urlObject['long'] !== null) {
                                    setLinkLng(urlObject['long'].toString());
                                }
                                if ('altitude' in urlObject && urlObject['altitude'] !== null) {
                                    setLinkAlt(urlObject['altitude'].toString());
                                }
                                if ('creation_time' in urlObject && urlObject['creation_time'] !== null) {
                                    setLinkCreationTime(urlObject['creation_time'].toString());
                                }

                                setFileLoaded(true);
                                setVideoProcessing(false);
                                setCompressionProgress(1);
                                return;
                            } else {
                                throw new Error('Server upload failed');
                            }
                        }

                        // Compressed file is small enough - use directly
                        // Create a File object from the blob
                        const compressedFile = new File([compressedBlob], `compressed_${uuidv4()}.mp4`, { type: 'video/mp4' });

                        // Set states in correct order - file first, then loaded flag
                        setSelectedFile(compressedFile as any);
                        setPreview(previewUrl);
                        setCompressionProgress(1);
                        setVideoProcessing(false);
                        setFileLoaded(true);

                        // Give React time to update before trying to play
                        setTimeout(() => {
                            if (previewVideoRef.current) {
                                if (platform === 'android') {
                                    // On Android, use blob URL and set src directly for proper thumbnail
                                    fetchVideoAndPlay(previewVideoRef, previewUrl);
                                } else {
                                    previewVideoRef.current.load();
                                    previewVideoRef.current.play().catch((e: Error) => console.log('Video autoplay blocked:', e));
                                    previewVideoRef.current.muted = !isNative;
                                }
                            }
                        }, 100);

                        return;
                    } catch (fetchError) {
                        const errorMessage = fetchError instanceof Error ? fetchError.message : 'Unknown error reading compressed video';
                        console.error('Error fetching/processing compressed video:', errorMessage, fetchError);
                        trackEvent('upload_compression_fetch_failed', {
                            error: errorMessage,
                            preview_url: previewUrl,
                            platform: platform
                        });
                        // Fall through to server compression fallback
                    }
                } else {
                    console.warn('Native compression failed:', compressionResult.error);
                }
            } catch (error) {
                console.error('Native compression error:', error);
                trackEvent('upload_compression_native_error', {
                    error: error instanceof Error ? error.message : 'Unknown error',
                    platform: platform
                });
                // Fall back to server compression as last resort (only if we have a blob)
                if (blob) {
                    console.log('Falling back to server compression after native error');
                } else {
                    // No blob available (native picker was used) - can't fall back
                    console.error('Native compression failed and no blob available for fallback');
                    presentToast("Compression failed. Please try again.");
                    setVideoProcessing(false);
                    return;
                }
            }
        }

        // Server-side compression fallback - for web platform OR if native compression failed
        // Only use if we have a blob to upload
        if (!blob) {
            console.error('No blob available for server compression');
            setVideoProcessing(false);
            presentToast("Failed to process video. Please try again.");
            return;
        }
        console.log('Using server-side compression');
        const urlObject = await compressVideoServer(blob);

        if (urlObject && urlObject['url'] !== '' && urlObject['url'] != null) {
            trackEvent('upload_compression_completed', {
                success: true,
                final_url: urlObject['url'],
                has_metadata: Boolean(urlObject['lat'] || urlObject['long'] || urlObject['altitude'])
            });
            const url = urlObject['url'];
            setFileIsLink(true);
            setFileLink(url);
            setPreview(url);

            if ('lat' in urlObject && urlObject['lat'] !== null) {
                setLinkLat(urlObject['lat'].toString());
            }

            if ('long' in urlObject && urlObject['long'] !== null) {
                setLinkLng(urlObject['long'].toString());
            }

            if ('altitude' in urlObject && urlObject['altitude'] !== null) {
                setLinkAlt(urlObject['altitude'].toString());
            }

            if ('creation_time' in urlObject && urlObject['creation_time'] !== null) {
                setLinkCreationTime(urlObject['creation_time'].toString());
            }

            setFileLoaded(true);
            setVideoProcessing(false);
            if (previewVideoRef.current) {
                await previewVideoRef.current.load();
                previewVideoRef.current.play();
                previewVideoRef.current.muted = !isNative;
            }
        } else {
            trackEvent('upload_compression_failed', {
                reason: 'invalid_url_or_null',
                platform: platform
            });
            setVideoProcessing(false);
            presentToast("Something went wrong when we were trying to compress your clip, try again or use a shorter clip");
        }
    }

    useEffect(() => {
        if (!selectedFile) {
            setPreview(undefined)
            return
        }

        const objectUrl = URL.createObjectURL(selectedFile);
        console.log("Object url is this: " + objectUrl);

        if (isNative && platform === "android") {
            const fileName = new Date().getTime() + '.mp4'
            const pathName = userUploadsDirectory + fileName;

            processVideoMobile(selectedFile, pathName);
        } else {
            processVideoWeb(objectUrl);
        }
        // free memory when ever this component is unmounted
        return () => URL.revokeObjectURL(objectUrl)
    }, [selectedFile]);

    // Location Dropdown
    const closeDropdown = useRef(null);
    // Filmer Dropdown
    const closeFilmerDropdown = useRef(null);

    const closeOpenMenus = (e: any) => {
        // Handle filmer dropdown
        if (filmerDropdown && !closeFilmerDropdown.current?.contains(e.target)) {
            const filmerContainer = closeFilmerDropdown.current?.closest('.filmer-container');
            if (!filmerContainer?.contains(e.target)) {
                setFilmerDropdown(false);
                if (!filmerInfo) {
                    setFilmerSearchText("");
                }
            }
        }

        if (locationDropdown && !closeDropdown.current?.contains(e.target)) {
            const locationContainer = closeDropdown.current?.closest('.location-container');
            if (!locationContainer?.contains(e.target)) {
                setLocationDropdown(false);
            }
        }

        // Handle Enter key
        if (e.key === 'Enter') {
            if (filmerDropdown) {
                setFilmerDropdown(false);
                if (!filmerInfo) {
                    setFilmerSearchText("");
                }
            }
            if (locationDropdown) {
                setLocationDropdown(false);
            }
        }
    }

    useEffect(() => {
        document.addEventListener("mousedown", closeOpenMenus);
        document.addEventListener('keypress', closeOpenMenus);

        return () => {
            document.removeEventListener("mousedown", closeOpenMenus);
            document.removeEventListener('keypress', closeOpenMenus);
        }
    }, [locationDropdown, filmerDropdown, filmerInfo]); // Add all dependencies

    const addHashtag = (item: any) => {
        trackEvent('upload_hashtag_added', {
            tag: item,
            total_tags: hashtags.length + 1
        });
        setHashtags(hashtags.concat(item));
        setSelectedTags(selectedTags.concat(item));
    }

    const removeHashtag = (item: any) => {
        trackEvent('upload_hashtag_removed', {
            tag: item,
            total_tags: hashtags.length - 1
        });
        const filtered = hashtags.filter((e: any) => e !== item);
        setHashtags(filtered);

        const filteredTags = hashtags.filter((e: any) => e !== item);
        setSelectedTags(filteredTags);
    }

    const addEventTag = (tag: string) => {
        trackEvent('upload_event_tag_added', { tag });
        setHashtags(hashtags.concat(tag));
        setSelectedTags(selectedTags.concat(tag));
    }

    const removeEventTag = (tag: string) => {
        trackEvent('upload_event_tag_removed', { tag });
        const filtered = hashtags.filter((e: any) => e !== tag);
        setHashtags(filtered);
        setSelectedTags(filtered);
    }

    // adds new item to multiselect
    const addTag = async (item: any) => {
        await Haptics.impact({ style: ImpactStyle.Light });
        setSelectedTags(selectedTags.concat(item));
        setShowDropdown(false);
    };

    // removes item from multiselect
    const removeTag = (item: any) => {
        const filtered = selectedTags.filter((e: any) => e !== item);
        setSelectedTags(filtered);
    }

    /**
     * Use native FilePicker on Android to get a file path directly.
     * This avoids the slow base64 conversion that causes the UI to freeze.
     */
    const pickVideoNative = () => {
        requirePhone(() => { pickVideoNativeInternal(); });
    };

    const pickVideoNativeInternal = async () => {
        try {
            await Haptics.impact({ style: ImpactStyle.Light });

            const result = await FilePicker.pickFiles({
                types: ['video/*'],
                limit: 1,
                readData: false, // Don't read data - we just need the path
            });

            if (!result.files || result.files.length === 0) {
                return;
            }

            const file = result.files[0];
            const fileSizeKiloBytes = (file.size || 0) / 1024;

            uploadStageRef.current = 'file_selected';
            trackEvent('upload_file_selected', {
                file_type: file.mimeType,
                file_size: fileSizeKiloBytes,
                platform: platform,
                picker: 'native'
            });

            if (fileSizeKiloBytes > MAX_FILE_SIZE) {
                trackEvent('upload_file_rejected', {
                    reason: 'file_too_large',
                    file_size: fileSizeKiloBytes,
                    max_size: MAX_FILE_SIZE
                });
                presentToast("The max file size must be less than 512MB so we can afford that sweet ramen");
                return;
            }

            if (!file.mimeType?.startsWith('video/')) {
                trackEvent('upload_file_rejected', {
                    reason: 'invalid_file_type',
                    file_type: file.mimeType
                });
                presentToast("Clips only!");
                return;
            }

            const NATIVE_COMPRESSION_THRESHOLD = 15360; // 15MB
            const shouldCompress = fileSizeKiloBytes > NATIVE_COMPRESSION_THRESHOLD;

            console.log(`Native picker - File size: ${fileSizeKiloBytes}KB, should compress: ${shouldCompress}`);
            console.log('Native file path:', file.path);

            if (shouldCompress && file.path) {
                uploadStageRef.current = 'compressing';
                trackEvent('upload_compression_started', {
                    original_size: fileSizeKiloBytes,
                    platform: platform,
                    compression_type: 'native_direct'
                });

                setVideoProcessing(true);
                setCompressionProgress(0);
                setCompressionPhase('compressing');

                // Pass the native path directly - no base64 conversion needed!
                await compressVideoForViewing(null, file.path);
            } else if (file.path) {
                // File is small enough, but we still need a blob for the upload
                // Read the file to get a blob
                console.log('Skipping compression - file already small enough');
                trackEvent('upload_compression_skipped', {
                    file_size: fileSizeKiloBytes,
                    platform: platform,
                    reason: 'file_under_threshold'
                });

                // For small files, we can read via Capacitor (still faster than base64 for large files)
                const previewUrl = Capacitor.convertFileSrc(file.path);
                try {
                    const response = await fetch(previewUrl);
                    const blob = await response.blob();
                    const videoFile = new File([blob], file.name || 'video.mp4', { type: file.mimeType || 'video/mp4' });
                    setSelectedFile(videoFile as any);
                    setPreview(previewUrl);
                    setFileLoaded(true);
                } catch (error) {
                    console.error('Error reading small file:', error);
                    presentToast("Failed to load video. Please try again.");
                }
            }
        } catch (error) {
            console.error('Native file picker error:', error);
            // If native picker fails, user can retry
            presentToast("Failed to pick video. Please try again.");
        }
    };

    const onSelectFile = async (event: any) => {
        trackEvent('upload_file_selected', {
            file_type: event.target.files?.[0]?.type,
            file_size: event.target.files?.[0]?.size / 1024, // size in KB
            platform: platform
        });

        if (!event.target.files || event.target.files.length === 0 || event.target.files.length > 1) {
            trackEvent('upload_file_rejected', {
                reason: 'invalid_selection',
                files_count: event.target.files?.length
            });
            setSelectedFile(undefined);
            return;
        }

        const fileSizeKiloBytes = event.target.files[0].size / 1024

        if (fileSizeKiloBytes > MAX_FILE_SIZE) {
            trackEvent('upload_file_rejected', {
                reason: 'file_too_large',
                file_size: fileSizeKiloBytes,
                max_size: MAX_FILE_SIZE
            });
            presentToast("The max file size must be less than 512MB so we can afford that sweet ramen");
            return;
        }

        if (!event.target.files[0].type.startsWith('video/')) {
            trackEvent('upload_file_rejected', {
                reason: 'invalid_file_type',
                file_type: event.target.files[0].type
            });
            presentToast("Clips only!");
            return;
        }

        const file = event.target.files[0];

        // iOS photo picker often already transcodes videos to a compatible format
        // Only compress if the file is still large (>15MB) to avoid double compression
        // For web, use the original threshold since there's no picker transcoding
        const NATIVE_COMPRESSION_THRESHOLD = 15360; // 15MB - iOS picker usually produces files under this

        const shouldCompress = isNative
            ? fileSizeKiloBytes > NATIVE_COMPRESSION_THRESHOLD
            : fileSizeKiloBytes > MAX_FILE_SIZE_WEB_PLAY;

        console.log(`File size: ${fileSizeKiloBytes}KB, threshold: ${isNative ? NATIVE_COMPRESSION_THRESHOLD : MAX_FILE_SIZE_WEB_PLAY}KB, should compress: ${shouldCompress}`);

        if (shouldCompress) {
            trackEvent('upload_compression_started', {
                original_size: fileSizeKiloBytes,
                platform: platform,
                compression_type: isNative ? 'native' : 'server'
            });

            // Show processing UI immediately so user knows something is happening
            setVideoProcessing(true);
            setCompressionProgress(0);
            setCompressionPhase('compressing');

            // For native platforms, we need the file path for native compression
            // Store the file temporarily and get its path
            if (isNative) {
                try {
                    // Use Cache directory directly (no subdirectory) to avoid "parent folder doesn't exist" error
                    const fileName = `temp_compress_${uuidv4()}.mp4`;

                    console.log('Storing video for compression:', fileName);

                    // Store the video file to get a file path for native compression
                    const base64Data = await base64FromBlob(file);
                    const savedFile = await Filesystem.writeFile({
                        path: fileName,
                        data: base64Data,
                        directory: Directory.Cache, // Use Cache directory - always exists
                    });

                    console.log('Video stored at:', savedFile.uri);

                    // Pass both the blob (for fallback) and the file path (for native compression)
                    await compressVideoForViewing(file, savedFile.uri);
                } catch (error) {
                    console.error('Error preparing file for native compression:', error);
                    // Show error to user instead of silently falling back to slow server compression
                    presentToast("Failed to prepare video for compression. Please try again.");
                    setVideoProcessing(false);
                }
            } else {
                // Web platform - use server compression
                await compressVideoForViewing(file);
            }
        } else {
            // File is small enough (iOS picker already transcoded or short clip)
            // Skip compression and use directly
            console.log('Skipping compression - file already small enough');
            trackEvent('upload_compression_skipped', {
                file_size: fileSizeKiloBytes,
                platform: platform,
                reason: 'file_under_threshold'
            });
            setSelectedFile(file);
            setFileLoaded(true);
        }
    }

    const analyzeEditLink = (inputStr: string) => {
        const youtubePattern = /(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)[\w-]+/;

        const isMatch = youtubePattern.test(inputStr);
        setIsValidLink(isMatch);

        if (isMatch) {
            setEditLink(inputStr);
        }
    }

    const goToVideoPreviewForEdit = () => {
        setFileLoaded(true);
    }

    // call the backend to upload the video based on the stored states
    const uploadVideo = async () => {
        const startTime = Date.now();
        setUploadStartTime(startTime);

        console.log("you've reached upload vid");
        setShowTrickForm(false);
        setVideoUploading(true);

        uploadStageRef.current = 'uploading';
        trackEvent('upload_started', {
            tags_count: selectedTags.length,
            has_location: Boolean(location),
            is_unlisted: unlisted,
            platform: platform
        });


        const data = new FormData();
        desiredBlobName ? data.append('file', selectedFile, desiredBlobName) : data.append('file', selectedFile);
        // @ts-ignore
        data.append('filename', selectedFile.value);

        const infd_id = userProfile.id;

        selectedTags.concat(hashtags);

        try {
            // go to the video playing url after upload
            const res: UserTrick = await userService.uploadUserTrick(infd_id, trickName, selectedTags,
                filmDate, location, sport, feature, trickCaption, data, locationInput, unlisted, segmentValue, filmerInfo);

            if (res == null) {
                console.log("upload failed!");
                trackEvent('upload_failed', {
                    reason: 'null_response',
                    platform: platform
                });
                presentToast("Upload failed :/ - reach out to devs @ecliptic.day")
                setVideoUploading(false);
            } else {
                console.log("upload successful!");
                uploadStageRef.current = 'completed';
                trackEvent('upload_completed', {
                    trick_id: res.trick_id,
                    user_id: res.user_id,
                    upload_duration: Date.now() - startTime,
                    platform: platform
                });
                await clearStatesOnUploadSuccess();
                history.push('/clip-upload/' + res.user_id + '/' + res.trick_id);
            }
        } catch (error) {
            // PII scrub: raw error messages can leak file paths, URLs, tokens.
            // Log a bounded error_code instead plus the NETWORK_ERROR event
            // so the errors dashboard can count upload failures separately.
            trackEvent('upload_error', {
                [Params.ERROR_CODE]: 'upload_failed',
                [Params.PLATFORM]: platform,
            });
            trackEvent(Events.NETWORK_ERROR, {
                [Params.ERROR_CODE]: 'upload_failed',
                [Params.ENDPOINT]: 'upload_user_trick',
                [Params.PLATFORM]: platform,
            });
            setVideoUploading(false);
            presentToast("Upload failed :/ - reach out to devs @ecliptic.day");
        }
    }

    // call the backend to upload the video based on the stored states
    const uploadVideoAsLink = async () => {
        const startTime = Date.now();
        setUploadStartTime(startTime);

        console.log("you've reached upload vid");

        trackEvent('upload_link_started', {
            link_type: 'direct',
            has_metadata: Boolean(linkLat || linkLng || linkAlt),
            has_creation_time: Boolean(linkCreationTime),
            tags_count: selectedTags.length,
            has_location: Boolean(location),
            is_unlisted: unlisted,
            platform: platform
        });

        setShowTrickForm(false);
        setVideoUploading(true);

        const infd_id = userProfile.id;

        try {
            // go to the video playing url after upload
            const res: UserTrick = await userService.uploadUserTrickAsLink(infd_id, trickName, selectedTags,
                filmDate, location, sport, feature, trickCaption, fileLink, locationInput, unlisted, linkLat, linkLng, linkAlt, linkCreationTime,
                segmentValue, filmerInfo);

            if (res == null) {
                console.log("upload failed!");
                trackEvent('upload_link_failed', {
                    reason: 'null_response',
                    platform: platform
                });

                presentToast("Upload failed :/ - reach out to devs @ecliptic.day");
                setVideoUploading(false);
            } else {
                console.log("upload successful!");
                trackEvent('upload_link_completed', {
                    trick_id: res.trick_id,
                    user_id: res.user_id,
                    upload_duration: Date.now() - startTime,
                    has_metadata: Boolean(linkLat || linkLng || linkAlt),
                    platform: platform
                });
                await clearStatesOnUploadSuccess();
                history.push('/clip-upload/' + res.user_id + '/' + res.trick_id);
            }
        } catch (error) {
            trackEvent('upload_link_error', {
                [Params.ERROR_CODE]: 'upload_link_failed',
                [Params.PLATFORM]: platform,
            });
            trackEvent(Events.NETWORK_ERROR, {
                [Params.ERROR_CODE]: 'upload_link_failed',
                [Params.ENDPOINT]: 'upload_user_trick_as_link',
                [Params.PLATFORM]: platform,
            });
            setVideoUploading(false);
            presentToast("Upload failed :/ - reach out to devs @ecliptic.day");
        }
    }

    // call the backend to upload the video based on the stored states
    const uploadEditAsLink = async () => {
        const startTime = Date.now();
        setUploadStartTime(startTime);

        console.log("you've reached upload vid");

        trackEvent('upload_edit_started', {
            edit_link: editLink,
            is_youtube: editLink.includes('youtube.com') || editLink.includes('youtu.be'),
            tags_count: selectedTags.length,
            has_location: Boolean(location),
            is_unlisted: unlisted,
            platform: platform
        });

        setShowTrickForm(false);
        setVideoUploading(true);

        const infd_id = userProfile.id;

        // go to the video playing url after upload
        try {
            const res: UserTrick = await userService.uploadUserTrickAsExternalLink(infd_id, trickName, selectedTags,
                filmDate, location, sport, feature, trickCaption, editLink, locationInput, unlisted, segmentValue, filmerInfo);

            if (res == null) {
                console.log("upload failed!");
                trackEvent('upload_edit_failed', {
                    reason: 'null_response',
                    platform: platform
                });
                presentToast("Upload failed :/ - reach out to devs @ecliptic.day");
                setVideoUploading(false);
            } else {
                console.log("upload successful!");
                trackEvent('upload_edit_completed', {
                    trick_id: res.trick_id,
                    user_id: res.user_id,
                    upload_duration: Date.now() - startTime,
                    is_youtube: editLink.includes('youtube.com') || editLink.includes('youtu.be'),
                    platform: platform
                });
                await clearStatesOnUploadSuccess();
                history.push('/clip-upload/' + res.user_id + '/' + res.trick_id);
            }
        } catch (error) {
            trackEvent('upload_edit_error', {
                [Params.ERROR_CODE]: 'upload_edit_failed',
                [Params.PLATFORM]: platform,
            });
            trackEvent(Events.NETWORK_ERROR, {
                [Params.ERROR_CODE]: 'upload_edit_failed',
                [Params.ENDPOINT]: 'upload_user_trick_as_external_link',
                [Params.PLATFORM]: platform,
            });
            setVideoUploading(false);
            presentToast("Upload failed :/ - reach out to devs @ecliptic.day");
        }
    }


    const clearStatesOnUploadSuccess = async () => {
        setFileLoaded(false);
        setFileIsLink(false);
        setFileLink(undefined);
        setVideoUploading(false);
        setShowTrickForm(false);
        setShowTagSelect(false);
        setSelectedFile(null);
        setDesiredBlobName(null);
        setVideoProcessing(false);
        setUnlisted(false);

        // Link Clear
        setLinkLat(undefined);
        setLinkAlt(undefined);
        setLinkLng(undefined);
        setLinkCreationTime(undefined);

        // Location
        setLocationDropdown(false);
        setLocationDropdownOptions([]);
        setLocationInput(null);
        setLocation("");
        setGooglePlacesSessionToken(uuidv4());

        // Filmer
        setFilmerInfo(null);
        setFilmerSearchText("");
        setFilmerDropdownOptions([]);
        setFilmerDropdown(false);

        setTrickCaption("");
        setTrickName("");
        if (userProfile.profile.sports.length === 1) {
            setSport(userProfile.profile.sports[0]);
        }
        setFeature("");
        setPreview("");
        setHashtags([]);
        setSelectedTags([]);
        setFilmDate(new Date(new Date().toDateString()).toISOString());
        const query = new URLSearchParams(locationReact.search);
        if (query.has("tag")) {
            query.delete('tag')
            history.replace({
                search: query.toString(),
            })
        }

        await PreferencesService.setLoadSuggestedTricks(LoadSuggestedTricksStatus.ONE);

        // Segment Value
        setDisplayClipInfoEntry(false);
        setDisplayTrickInfoEntry(false);
        setDisplayFilmerInfoEntry(false);

        // Edits
        setEditLink("");
        setIsValidLink(false);

        // Time
        setUploadStartTime(0);
    }

    const setTrickLocation = async (location: string) => {
        trackEvent('upload_location_entered', {
            location: location,
            has_previous_token: Boolean(googlePlacesSessionToken)
        });
        let currGooglePlacesToken = googlePlacesSessionToken;

        if (location === '' || location === null) {
            currGooglePlacesToken = uuidv4();
            setGooglePlacesSessionToken(currGooglePlacesToken);
        }

        setLocation(location);
        await queryTrickLocation(location, currGooglePlacesToken);
    }

    const queryFilmer = async (filmerQuery: string) => {
        if (!userProfile?.id || !filmerQuery.trim()) return;

        const res = await SearchService.searchUserProfiles(filmerQuery, 5, 0, userProfile.id);
        const filmerOptions: UserProfileSearch[] = res?.map((profile: any) => ({
            username: profile.username,
            user_id: profile.id,
            profile_picture_url: profile.profile_pic
        })) || [];

        setFilmerDropdownOptions(filmerOptions);
        if (filmerOptions.length > 0) {
            setFilmerDropdown(true);
        }
    };

    const handleFilmerSearch = async (searchText: string) => {
        setFilmerSearchText(searchText);
        setFilmerInfo(null); // Reset filmer info when user starts a new search
        await queryFilmer(searchText);
    };

    const addFilmerItem = (selectedFilmer: any) => {
        setFilmerInfo({
            username: selectedFilmer.username,
            user_id: selectedFilmer.user_id
        });
        setFilmerSearchText(selectedFilmer.username);
        setFilmerDropdown(false);

        trackEvent('upload_filmer_selected', {
            filmer_username: selectedFilmer.username
        });
    };

    const queryTrickLocation = async (location: string, currGooglePlacesToken: string) => {
        const res = await LocationService.getLocationFromPlacesAPI(location, currGooglePlacesToken);
        const locationDropdownOptions: LocationInput[] = res.map((location: any) => {
            const locationInput: LocationInput = {
                description: location.description,
                main_text: location.structured_formatting.main_text,
                place_id: location.place_id,
            }
            return locationInput;
        });
        console.log(locationDropdownOptions);
        setLocationDropdownOptions(locationDropdownOptions);
        if (locationDropdownOptions.length > 0) {
            setLocationDropdown(true);
        }
    }

    const addLocationItem = (locationInput: LocationInput) => {
        setLocationInput(locationInput);
        setLocation(locationInput.main_text);
        setLocationDropdown(false);
    }

    const onHandleShowTrickForm = async () => {
        trackEvent('upload_form_opened', {
            file_loaded: fileLoaded,
            is_link: fileIsLink
        });
        await Haptics.impact({ style: ImpactStyle.Light });
        setShowTrickForm(true);
        // get url parameters if any for events
        const query = new URLSearchParams(locationReact.search);
        if (query.has("tag")) {
            const hashtag = "#" + query.get("tag");
            addHashtag(hashtag);
        }
    }

    const onSetUnlisted = (checked: boolean) => {
        console.log("unlisted: " + checked)
        setUnlisted(checked);
    }

    // GENERIC
    const handleSegmentChange = (segement: string) => {
        console.log("Segment Changed: " + segement);
        setSegmentValue(segement);
    }

    const toggleFilmerInfo = () => {
        setDisplayFilmerInfoEntry(!displayFilmerInfoEntry);
    }

    const toggleClipInfo = () => {
        setDisplayClipInfoEntry(!displayClipInfoEntry);
    }

    const toggleTrickInfo = () => {
        setDisplayTrickInfoEntry(!displayTrickInfoEntry);
    }

    const performUpload = async () => {
        await Haptics.impact({ style: ImpactStyle.Light });
        if (segmentValue === ClipType.LINE) {
            if (!fileIsLink) {
                await uploadVideo();
            } else {
                await uploadVideoAsLink();
            }
        } else if (segmentValue === ClipType.EDIT) {
            if (!fileIsLink && editLink === '') {
                await uploadVideo();
            } else if (fileIsLink && editLink === '') {
                await uploadVideoAsLink();
            } else if (editLink !== '') {
                await uploadEditAsLink();
            } else {
                await uploadVideo();
            }
        } else {
            await uploadVideo();
        }
    }

    const handleUploadVideoFromFileOrLink = async () => {
        requirePhone(() => { performUpload(); });
    }

    const presentToast = (message: string) => {
        present({
            message: message,
            duration: 5000,
            position: 'top',
            icon: alertCircleOutline,
            buttons: [
                {
                    text: 'Dismiss',
                    role: 'cancel',
                    handler: () => {
                        console.log('Dismiss clicked');
                    },
                },
            ],
            onDidDismiss: (e: CustomEvent) => console.log(`Dismissed with role: ${e.detail.role}`)
        });
    };

    const getHeaderText = () => {
        switch (segmentValue) {
            case ClipType.CLIP:
                return "Upload a clip/trick";
            case ClipType.LINE:
                return "Upload a line";
            case ClipType.EDIT:
                return "Upload an edit";
            default:
                return "Upload a clip/trick";
        }
    };


    // const handleGoToClip = async () => {
    //     await invalidateQueries(); -> for some reason this didn't work!!! 
    //     const userId = uploadedUserTrick.user_id;
    //     const trickId = uploadedUserTrick.trick_id;
    //     // Set up a one-time listener for after navigation
    //     const unlisten = history.listen(() => {
    //         clearStatesOnUploadSuccess();
    //         unlisten(); // Remove the listener after it fires once
    //     });

    //     history.push(`/ clip - upload / ${ userId } / ${ trickId }`);
    // }

    if (isLoading) {
        return (<LoadingPage />);
    }

    if (!isAuthenticated) {
        return (
            <IonPage>
                <CustomHelmet title={"Ecliptic // Upload"}
                    description={"Upload your clips to Ecliptic to build your bag of tricks and get noticed by the crew, sponsors, or the world"}
                    url={`https://ecliptic.day/upload`} />
                <LoginButtonPage
                    title="Build Your Bag Tricks To Get Noticed by the Squad, Sponsors, and More"
                    upload={false} profile={true} brand={false} />)
            </IonPage>
        );
    }

    if (videoUploading) {
        return (
            <IonPage>
                <IonHeader translucent={true}>
                </IonHeader>
                <IonContent>
                    <div className="text-2xl text-center mt-20 mb-8">
                        {getHeaderText()}
                    </div>
                    <div className="flex flex-col justify-center items-center px-8" style={{ marginTop: '30vh' }}>
                        <div className="w-full max-w-sm">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-base" style={{ color: '#0affff' }}>
                                    {processingVerb}...
                                </span>
                            </div>
                            <IonProgressBar
                                type="indeterminate"
                                style={{
                                    '--progress-background': '#0affff',
                                    '--background': 'rgba(255,255,255,0.1)',
                                    height: '6px',
                                    borderRadius: '3px'
                                } as React.CSSProperties}
                            ></IonProgressBar>
                        </div>
                    </div>
                </IonContent>
            </IonPage>
        );
    } else if (videoProcessing) {
        return (
            <IonPage>
                <IonHeader translucent={true}>
                </IonHeader>
                <IonContent>
                    <div className="text-2xl text-center mt-20 mb-8">
                        {getHeaderText()}
                    </div>
                    <div className="flex flex-col justify-center items-center px-8" style={{ marginTop: '30vh' }}>
                        <div className="w-full max-w-sm">
                            {compressionPhase === 'uploading' ? (
                                <>
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-base" style={{ color: '#0affff' }}>
                                            {processingVerb}...
                                        </span>
                                    </div>
                                    <IonProgressBar
                                        type="indeterminate"
                                        style={{
                                            '--progress-background': '#0affff',
                                            '--background': 'rgba(255,255,255,0.1)',
                                            height: '6px',
                                            borderRadius: '3px'
                                        } as React.CSSProperties}
                                    ></IonProgressBar>
                                </>
                            ) : compressionProgress > 0 ? (
                                <>
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-base" style={{ color: '#5861AC' }}>
                                            {processingVerb}...
                                        </span>
                                        <span className="text-base tabular-nums" style={{ color: '#5861AC' }}>
                                            {Math.round(compressionProgress * 100)}%
                                        </span>
                                    </div>
                                    <IonProgressBar
                                        value={compressionProgress}
                                        style={{
                                            '--progress-background': '#5861AC',
                                            '--background': 'rgba(255,255,255,0.1)',
                                            height: '6px',
                                            borderRadius: '3px'
                                        } as React.CSSProperties}
                                    ></IonProgressBar>
                                </>
                            ) : (
                                <>
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-base" style={{ color: '#5861AC' }}>
                                            {processingVerb}...
                                        </span>
                                    </div>
                                    <IonProgressBar
                                        type="indeterminate"
                                        style={{
                                            '--progress-background': '#5861AC',
                                            '--background': 'rgba(255,255,255,0.1)',
                                            height: '6px',
                                            borderRadius: '3px'
                                        } as React.CSSProperties}
                                    ></IonProgressBar>
                                </>
                            )}
                        </div>
                    </div>
                </IonContent>
            </IonPage>
        );
    } else if (showTagSelect && segmentValue === ClipType.CLIP) {
        return (
            <IonPage>
                <IonHeader translucent={true}>
                </IonHeader>
                <IonContent>
                    <div className="text-2xl text-center items-center justify-center mt-20">
                        Upload a clip/trick for {state.user?.username ? state.user?.username : userProfile.username}
                    </div>
                    <div className="flex justify-center items-center w-full mt-8">
                        <form className="w-10/12 lg:w-8/12">
                            <h2 className="text-2xl font-bold tracking-wide">Select Your Tags</h2>
                            <div className="mb-6">
                                <TagSelectOnUpload sport={[sport]} feature={[feature]} parentAddTag={addTag}
                                    parentRemoveTag={removeTag} />
                            </div>
                            <div className="flex justify-center items-center gap-x-4 mb-10">
                                <IonButton className="neon-button"
                                    onClick={() => setShowTagSelect(false)}>Back</IonButton>
                                <IonButton className="neon-button-alt" onClick={() => {
                                    if (!fileIsLink) {
                                        uploadVideo();
                                    } else {
                                        uploadVideoAsLink();
                                    }
                                }}>Upload</IonButton>
                            </div>
                        </form>
                    </div>
                </IonContent>
            </IonPage>
        );
    } else if (showTagSelect && (segmentValue === ClipType.LINE || segmentValue === ClipType.EDIT)) {
        return (
            <IonPage>
                <IonHeader translucent={true}>
                </IonHeader>
                <IonContent>
                    {
                        segmentValue === ClipType.LINE ?
                            <div className="text-2xl text-center items-center justify-center mt-20">
                                Upload a line
                            </div>
                            : segmentValue === ClipType.EDIT ?
                                <div className="text-2xl text-center items-center justify-center mt-20">
                                    Upload an edit
                                </div> :
                                <div className="text-2xl text-center items-center justify-center mt-20">
                                    Upload a clip/trick
                                </div>
                    }
                    <div className="flex justify-center items-center w-full mt-8">
                        <form className="w-10/12 lg:w-8/12">
                            <h2 className="text-2xl font-bold tracking-wide">Select Your Tags</h2>
                            <div className="mb-6">
                                <TagSelectOnUpload sport={[sport]} feature={[feature]} parentAddTag={addTag}
                                    parentRemoveTag={removeTag} tagsCombinedToOne={true}
                                    currentTags={selectedTags} />
                            </div>
                            <div className="flex justify-center items-center gap-x-4 mb-10">
                                <IonButton className="neon-button"
                                    onClick={() => setShowTagSelect(false)}>Back</IonButton>
                                <IonButton className="neon-button-alt"
                                    onClick={handleUploadVideoFromFileOrLink}>Upload</IonButton>
                            </div>
                        </form>
                    </div>
                </IonContent>
            </IonPage>
        );
    } else if (fileLoaded && !showTrickForm) {
        return (
            <IonPage>
                <IonHeader translucent={true}>
                </IonHeader>
                <IonContent>
                    <div className="text-2xl text-center items-center justify-center mt-20">
                        {getHeaderText()}
                    </div>
                    <div className="flex justify-center items-center my-10">
                        {
                            ClipType.EDIT && editLink !== "" && editLink !== undefined ?
                                <div
                                    className="flex flex-col justify-center items-center xs:h-full xs:w-full sm:w-10/12 sm:h-10/12 md:w-9/12 md:h-9/12 l:w-8/12 l:h-8/12 xl:w-7/12 xl:h-7/12">
                                    <ReactPlayer url={editLink} controls={true} width={"100%"}></ReactPlayer>
                                </div> :
                                <video ref={previewVideoRef}
                                    playsInline
                                    webkit-playsinline="true"
                                    autoPlay={false}
                                    className="flex flex-col justify-center items-center xs:h-full xs:w-full sm:w-10/12 sm:h-10/12 md:w-9/12 md:h-9/12 l:w-8/12 l:h-8/12 xl:w-7/12 xl:h-7/12"
                                    controls preload="auto">
                                    <source src={!fileIsLink ? preview : fileLink} type="video/mp4" />
                                </video>
                        }
                    </div>
                    <div className="flex justify-center items-center gap-x-4">
                        <IonButton className="mr-4 neon-button" onClick={async () => {
                            // Clean up video element
                            if (previewVideoRef.current) {
                                previewVideoRef.current.pause();
                                previewVideoRef.current.removeAttribute('src');
                                previewVideoRef.current.load();
                            }
                            // Cancel any ongoing compression
                            try {
                                await CapacitorVideoCompression.cancelCompression({});
                            } catch (e) {
                                // Ignore if no compression is running
                            }
                            // Clean up preview URL if it's a blob
                            if (preview && preview.startsWith('blob:')) {
                                URL.revokeObjectURL(preview);
                            }
                            setSelectedFile(undefined);
                            setPreview("");
                            setFileLoaded(false);
                            setDesiredBlobName(null);
                            setFileIsLink(false);
                            setFileLink(undefined);
                            setEditLink("");
                            setIsValidLink(false);
                            setCompressionProgress(0);
                            setVideoProcessing(false);
                        }}>
                            Back</IonButton>
                        <IonButton onClick={() => onHandleShowTrickForm()} className="neon-button-alt">Next</IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    } else if (showTrickForm && segmentValue === ClipType.CLIP) {
        const currentDate = new Date(new Date().toDateString()).toISOString();
        return (
            <IonPage>
                <IonHeader translucent={true}>
                </IonHeader>
                <IonContent>
                    <div className="text-2xl text-center items-center justify-center mt-20">
                        Upload a clip/trick
                    </div>
                    <div className="flex justify-center items-center w-full mt-8">
                        <form className="w-10/12 lg:w-8/12">
                            <h2 className="text-xl font-bold tracking-wide mb-2">Details</h2>
                            <div className="mb-6">
                                <div className="flex flex-row items-center">
                                    <label className="block mb-2 text-lg font-medium">
                                        Trick Name
                                    </label>
                                </div>

                                <input
                                    className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ion-text-area-upload-clip"
                                    placeholder={'Call your style'}
                                    required
                                    value={trickName}
                                    onChange={(e) => {
                                        setTrickName(e.target.value);
                                        // setOptions(e.target.value.split(" "));
                                    }}
                                />
                            </div>
                            <div className="mb-6">
                                <div className="flex flex-row items-center">
                                    <label className="block mb-2 text-lg font-medium">
                                        Caption
                                    </label>
                                </div>
                                <IonTextarea autoGrow={true} value={trickCaption}
                                    className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full px-2.5 ion-text-area-upload-clip"
                                    placeholder="Optional but go crazy" rows={1}
                                    onIonInput={(e) => setTrickCaption(e.detail.value as string)}></IonTextarea>
                            </div>
                            <div className="mb-5">
                                <div className="flex flex-row items-center">
                                    <label className="block mb-2 text-lg font-medium">
                                        Hashtags
                                    </label>
                                </div>
                                <label className="block mb-1 text-sm font-medium text-gray-400">
                                    Events
                                </label>
                                <EventHashtagSelect
                                    selectedEventTags={hashtags.filter((h: string) => h.startsWith('#'))}
                                    addEventTag={addEventTag}
                                    removeEventTag={removeEventTag}
                                />
                                <label className="block mb-1 mt-2 text-sm font-medium text-gray-400">
                                    Custom
                                </label>
                                <MultiSelectHashtag
                                    addTag={addHashtag}
                                    removeTag={removeHashtag}
                                    selectedOptions={hashtags}
                                    placeholder={'For brands, swaq, etc.. (click tag for trick tags)'}
                                />
                            </div>
                            <div className="flex flex-col mb-3">
                                <div className="flex flex-row justify-between items-center mb-2">
                                    <label className="block text-xl font-medium">Filmer Info</label>
                                    <IDXButtonIcon size={"text-2xl"} icon={chevronDownOutline}
                                        onClick={toggleFilmerInfo} />
                                </div>
                                {
                                    displayFilmerInfoEntry ?
                                        <>
                                            <div className="mb-6 ml-2 filmer-container" ref={closeFilmerDropdown}>
                                                <label className="block mb-2 text-lg font-medium">
                                                    Username
                                                </label>
                                                <input
                                                    className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ion-text-area-upload-clip"
                                                    placeholder="moonjibs"
                                                    value={filmerSearchText}
                                                    onChange={(e) => handleFilmerSearch(e.target.value)}
                                                />
                                                {filmerDropdown && (
                                                    <Dropdown
                                                        list={filmerDropdownOptions}
                                                        addItem={addFilmerItem}
                                                        accessProperty={true}
                                                        accessPropertyString={'username'}
                                                    />
                                                )}
                                            </div>
                                        </> : null
                                }
                            </div>
                            <div className="flex flex-col mb-3">
                                <div className="flex flex-row justify-between items-center mb-2">
                                    <label className="block text-xl font-medium">Trick Info</label>
                                    <IDXButtonIcon size={"text-2xl"} icon={chevronDownOutline}
                                        onClick={toggleTrickInfo} />
                                </div>
                                {
                                    displayTrickInfoEntry ?
                                        <>
                                            <div className="mb-6 ml-2">
                                                <label className="block mb-2 text-lg font-medium">Sport</label>
                                                <div className="relative w-full lg:max-w-sm">
                                                    <select
                                                        value={sport}
                                                        onChange={(event) => {
                                                            setSport(event.target.value);
                                                        }}
                                                        className="w-full p-2.5 text-gray-500 bg-white border rounded-md shadow-sm outline-none appearance-none focus:border-indigo-600"
                                                    >
                                                        <option selected hidden>Select a sport</option>
                                                        {sportOptions.map((sport) => (
                                                            <option id={sport.id.toString()}>{sport.name}</option>))}
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="ml-2 mb-3">
                                                <label className="block mb-2 text-lg font-medium">Feature</label>
                                                <div className="relative w-full lg:max-w-sm">
                                                    <select
                                                        value={feature}
                                                        onChange={(event) => {
                                                            setFeature(event.target.value);
                                                        }}
                                                        className="w-full p-2.5 text-gray-500 bg-white border rounded-md shadow-sm outline-none appearance-none focus:border-indigo-600"
                                                    >
                                                        <option selected hidden>Select a feature</option>
                                                        {sport === "Other" ? otherFeatures.map((feature) => (<option
                                                            id={feature.id.toString()}>{feature.name}</option>)) : features.map((feature) => (
                                                                <option
                                                                    id={feature.id.toString()}>{feature.name}</option>))}
                                                    </select>
                                                </div>
                                            </div>
                                        </> : null
                                }
                            </div>
                            <div className="flex flex-col mb-3">
                                <div className="flex flex-row justify-between items-center mb-2">
                                    <label className="block text-xl font-medium">Clip Info</label>
                                    <IDXButtonIcon size={"text-2xl"} icon={chevronDownOutline}
                                        onClick={toggleClipInfo} />
                                </div>
                                {
                                    displayClipInfoEntry ?
                                        <>
                                            <div className="mb-6 ml-2 location-container" ref={closeDropdown}>
                                                <label className="block mb-2 text-lg font-medium">
                                                    Location
                                                    <IonIcon icon={informationCircleOutline} color="theme-alternative"
                                                        className="ml-2"
                                                        onClick={() => presentToast("If you want your clip location private, don't enter one, we do not sell your data :)")}>
                                                    </IonIcon>
                                                </label>
                                                <input
                                                    className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ion-text-area-upload-clip"
                                                    placeholder="mountain or city"
                                                    value={location}
                                                    onChange={(e) => setTrickLocation(e.target.value)}
                                                />
                                                {locationDropdown ?
                                                    <Dropdown list={locationDropdownOptions}
                                                        addItem={addLocationItem}
                                                        accessProperty={true}
                                                        accessPropertyString={'description'}></Dropdown> : null}
                                            </div>
                                            <div className="ml-2 mb-3">
                                                <label className="block mb-2 text-lg font-medium">Date Filmed</label>
                                                <IonDatetimeButton className="justify-start" datetime="datetime" />
                                                <IonModal keepContentsMounted={true}>
                                                    <IonDatetime id="datetime" presentation="date" color="dark"
                                                        value={filmDate} max={currentDate}
                                                        onIonChange={(event) => setFilmDate(event.target.value.toString())} />
                                                </IonModal>
                                                <p id="helper-text-explanation"
                                                    className="mt-3 text-sm text-gray-500">If you aren't sure when
                                                    you filmed this trick, just select today's date.</p>
                                            </div>
                                            <div className="mb-6 ml-2">
                                                <IonCheckbox labelPlacement="start"
                                                    onIonChange={(ev) => onSetUnlisted(ev.detail.checked)}
                                                    className="text-lg">
                                                    {FOLLOWERS_ONLY}
                                                </IonCheckbox>
                                            </div>
                                        </> : null
                                }
                            </div>
                            <div>
                                <div className="flex flex-row justify-between mb-10">
                                    <IonButton className="neon-button" onClick={() => {
                                        setShowTrickForm(false)
                                    }
                                    }>Back</IonButton>
                                    <IonButton className="neon-button-highlight" onClick={async () => {
                                        if (trickName && sport && feature) {
                                            await Haptics.impact({ style: ImpactStyle.Light });
                                            setShowTagSelect(true);
                                            setDisplayTrickInfoEntry(false);
                                            setDisplayClipInfoEntry(false);
                                            setDisplayFilmerInfoEntry(false);
                                        } else {
                                            presentToast("Please fill trick info fields to tag your clip!");
                                        }
                                    }}>Tag</IonButton>
                                    <IonButton className="neon-button-alt" onClick={async () => {
                                        if (trickName) {
                                            if (!fileIsLink) {
                                                await Haptics.impact({ style: ImpactStyle.Light });
                                                uploadVideo();
                                            } else {
                                                await Haptics.impact({ style: ImpactStyle.Light });
                                                uploadVideoAsLink();
                                            }
                                        } else {
                                            presentToast("Please fill out the trick name and other fields if desired!");
                                        }
                                    }}>Upload</IonButton>
                                </div>
                            </div>
                        </form>
                    </div>
                </IonContent>
            </IonPage>
        );
    } else if (showTrickForm && segmentValue === ClipType.LINE) {
        const currentDate = new Date(new Date().toDateString()).toISOString();
        return (
            <IonPage>
                <IonHeader translucent={true}>
                </IonHeader>
                <IonContent>
                    <div className="text-2xl text-center items-center justify-center mt-20">
                        Upload a line
                    </div>
                    <div className="flex justify-center items-center w-full mt-8">
                        <form className="w-10/12 lg:w-8/12">
                            <h2 className="text-xl font-bold tracking-wide mb-2">Details</h2>
                            <div className="mb-6">
                                <div className="flex flex-row items-center">
                                    <label className="block mb-2 text-lg font-medium">
                                        Line Name
                                    </label>
                                </div>

                                <input
                                    className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ion-text-area-upload-clip"
                                    placeholder={'Call your style'}
                                    required
                                    value={trickName}
                                    onChange={(e) => {
                                        setTrickName(e.target.value);
                                        // setOptions(e.target.value.split(" "));
                                    }}
                                />
                            </div>
                            <div className="mb-6">
                                <div className="flex flex-row items-center">
                                    <label className="block mb-2 text-lg font-medium">
                                        Caption
                                    </label>
                                </div>
                                <IonTextarea autoGrow={true} value={trickCaption}
                                    className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full px-2.5 ion-text-area-upload-clip"
                                    placeholder="Optional but go crazy on this one" rows={1}
                                    onIonInput={(e) => setTrickCaption(e.detail.value as string)}></IonTextarea>
                            </div>
                            <div className="mb-5">
                                <div className="flex flex-row items-center">
                                    <label className="block mb-2 text-lg font-medium">
                                        Hashtags
                                    </label>
                                </div>
                                <label className="block mb-1 text-sm font-medium text-gray-400">
                                    Events
                                </label>
                                <EventHashtagSelect
                                    selectedEventTags={hashtags.filter((h: string) => h.startsWith('#'))}
                                    addEventTag={addEventTag}
                                    removeEventTag={removeEventTag}
                                />
                                <label className="block mb-1 mt-2 text-sm font-medium text-gray-400">
                                    Custom
                                </label>
                                <MultiSelectHashtag
                                    addTag={addHashtag}
                                    removeTag={removeHashtag}
                                    selectedOptions={hashtags}
                                    placeholder={'For brands, swaq, etc.. (click tag for trick tags)'}
                                />
                            </div>
                            <div className="flex flex-col mb-3">
                                <div className="flex flex-row justify-between items-center mb-2">
                                    <label className="block text-xl font-medium">Filmer Info</label>
                                    <IDXButtonIcon size={"text-2xl"} icon={chevronDownOutline}
                                        onClick={toggleFilmerInfo} />
                                </div>
                                {
                                    displayFilmerInfoEntry ?
                                        <>
                                            <div className="mb-6 ml-2 filmer-container" ref={closeFilmerDropdown}>
                                                <label className="block mb-2 text-lg font-medium">
                                                    Username
                                                </label>
                                                <input
                                                    className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ion-text-area-upload-clip"
                                                    placeholder="moonjibs"
                                                    value={filmerSearchText}
                                                    onChange={(e) => handleFilmerSearch(e.target.value)}
                                                />
                                                {filmerDropdown && (
                                                    <Dropdown
                                                        list={filmerDropdownOptions}
                                                        addItem={addFilmerItem}
                                                        accessProperty={true}
                                                        accessPropertyString={'username'}
                                                    />
                                                )}
                                            </div>
                                        </> : null
                                }
                            </div>
                            <div className="flex flex-col mb-3">
                                <div className="flex flex-row justify-between items-center mb-2">
                                    <label className="block text-xl font-medium">Line Info</label>
                                    <IDXButtonIcon size={"text-2xl"} icon={chevronDownOutline}
                                        onClick={toggleTrickInfo} />
                                </div>
                                {
                                    displayTrickInfoEntry ?
                                        <>
                                            <div className="mb-6 ml-2">
                                                <label className="block mb-2 text-lg font-medium">Sport</label>
                                                <div className="relative w-full lg:max-w-sm">
                                                    <select
                                                        value={sport}
                                                        onChange={(event) => {
                                                            setSport(event.target.value);
                                                        }}
                                                        className="w-full p-2.5 text-gray-500 bg-white border rounded-md shadow-sm outline-none appearance-none focus:border-indigo-600"
                                                    >
                                                        <option selected hidden>Select a sport</option>
                                                        {sportOptions.map((sport) => (
                                                            <option id={sport.id.toString()}>{sport.name}</option>))}
                                                    </select>
                                                </div>
                                            </div>
                                        </> : null
                                }
                            </div>
                            <div className="flex flex-col mb-3">
                                <div className="flex flex-row justify-between items-center mb-2">
                                    <label className="block text-xl font-medium">Clip Info</label>
                                    <IDXButtonIcon size={"text-2xl"} icon={chevronDownOutline}
                                        onClick={toggleClipInfo} />
                                </div>
                                {
                                    displayClipInfoEntry ?
                                        <>
                                            <div className="mb-6 ml-2 location-container" ref={closeDropdown}>
                                                <label className="block mb-2 text-lg font-medium">
                                                    Location
                                                    <IonIcon icon={informationCircleOutline} color="theme-alternative"
                                                        className="ml-2"
                                                        onClick={() => presentToast("If you want your clip location private, don't enter one, we do not sell your data :)")}>
                                                    </IonIcon>
                                                </label>
                                                <input
                                                    className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ion-text-area-upload-clip"
                                                    placeholder="mountain or city"
                                                    value={location}
                                                    onChange={(e) => setTrickLocation(e.target.value)}
                                                />
                                                {locationDropdown ?
                                                    <Dropdown list={locationDropdownOptions} addItem={addLocationItem}
                                                        accessProperty={true}
                                                        accessPropertyString={'description'}></Dropdown> : null}
                                            </div>
                                            <div className="ml-2 mb-3">
                                                <label className="block mb-2 text-lg font-medium">Date Filmed</label>
                                                <IonDatetimeButton className="justify-start" datetime="datetime" />
                                                <IonModal keepContentsMounted={true}>
                                                    <IonDatetime id="datetime" presentation="date" color="dark"
                                                        value={filmDate} max={currentDate}
                                                        onIonChange={(event) => setFilmDate(event.target.value.toString())} />
                                                </IonModal>
                                                <p id="helper-text-explanation"
                                                    className="mt-3 text-sm text-gray-500">If you aren't sure when
                                                    you filmed this line, just select today's date.</p>
                                            </div>
                                            <div className="mb-6 ml-2">
                                                <IonCheckbox labelPlacement="start"
                                                    onIonChange={(ev) => onSetUnlisted(ev.detail.checked)}
                                                    className="text-lg">
                                                    {FOLLOWERS_ONLY}
                                                </IonCheckbox>
                                            </div>
                                        </> : null
                                }
                            </div>
                            <div>
                                <div className="flex flex-row justify-between mb-10">
                                    <IonButton className="neon-button" onClick={() => {
                                        setShowTrickForm(false);
                                        setDisplayTrickInfoEntry(false);
                                        setDisplayClipInfoEntry(false);
                                        setDisplayFilmerInfoEntry(false);
                                    }}>Back</IonButton>
                                    <IonButton className="neon-button-highlight" onClick={async () => {
                                        if (trickName && sport) {
                                            await Haptics.impact({ style: ImpactStyle.Light });
                                            setShowTagSelect(true);
                                        } else {
                                            presentToast("Please fill line info fields to tag your line!");
                                        }
                                    }}>Tag</IonButton>
                                    <IonButton className="neon-button-alt" onClick={async () => {
                                        if (trickName) {
                                            await Haptics.impact({ style: ImpactStyle.Light });
                                            if (!fileIsLink) {
                                                uploadVideo();
                                            } else {
                                                uploadVideoAsLink();
                                            }
                                        } else {
                                            presentToast("Please fill out the trick name and other fields if desired!");
                                        }
                                    }}>Upload</IonButton>
                                </div>
                            </div>
                        </form>
                    </div>
                </IonContent>
            </IonPage>
        );
    } else if (showTrickForm && segmentValue === ClipType.EDIT) {
        const currentDate = new Date(new Date().toDateString()).toISOString();
        return (
            <IonPage>
                <IonHeader translucent={true}>
                </IonHeader>
                <IonContent>
                    <div className="text-2xl text-center items-center justify-center mt-20">
                        Upload an edit
                    </div>
                    <div className="flex justify-center items-center w-full mt-8">
                        <form className="w-10/12 lg:w-8/12">
                            <h2 className="text-xl font-bold tracking-wide mb-2">Details</h2>
                            <div className="mb-6">
                                <div className="flex flex-row items-center">
                                    <label className="block mb-2 text-lg font-medium">
                                        Edit Name
                                    </label>
                                </div>

                                <input
                                    className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ion-text-area-upload-clip"
                                    placeholder={'Call your style'}
                                    required
                                    value={trickName}
                                    onChange={(e) => {
                                        setTrickName(e.target.value);
                                        // setOptions(e.target.value.split(" "));
                                    }}
                                />
                            </div>
                            <div className="mb-6">
                                <div className="flex flex-row items-center">
                                    <label className="block mb-2 text-lg font-medium">
                                        Caption
                                    </label>
                                </div>
                                <IonTextarea autoGrow={true} value={trickCaption}
                                    className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full px-2.5 ion-text-area-upload-clip"
                                    placeholder="Optional but go crazy on this one" rows={1}
                                    onIonInput={(e) => setTrickCaption(e.detail.value as string)}></IonTextarea>
                            </div>
                            <div className="mb-5">
                                <div className="flex flex-row items-center">
                                    <label className="block mb-2 text-lg font-medium">
                                        Hashtags
                                    </label>
                                </div>
                                <label className="block mb-1 text-sm font-medium text-gray-400">
                                    Events
                                </label>
                                <EventHashtagSelect
                                    selectedEventTags={hashtags.filter((h: string) => h.startsWith('#'))}
                                    addEventTag={addEventTag}
                                    removeEventTag={removeEventTag}
                                />
                                <label className="block mb-1 mt-2 text-sm font-medium text-gray-400">
                                    Custom
                                </label>
                                <MultiSelectHashtag
                                    addTag={addHashtag}
                                    removeTag={removeHashtag}
                                    selectedOptions={hashtags}
                                    placeholder={'For brands, swaq, etc.. (click tag for trick tags)'}
                                />
                            </div>
                            <div className="flex flex-col mb-3">
                                <div className="flex flex-row justify-between items-center mb-2">
                                    <label className="block text-xl font-medium">Filmer Info</label>
                                    <IDXButtonIcon size={"text-2xl"} icon={chevronDownOutline}
                                        onClick={toggleFilmerInfo} />
                                </div>
                                {
                                    displayFilmerInfoEntry ?
                                        <>
                                            <div className="mb-6 ml-2 filmer-container" ref={closeFilmerDropdown}>
                                                <label className="block mb-2 text-lg font-medium">
                                                    Username
                                                </label>
                                                <input
                                                    className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ion-text-area-upload-clip"
                                                    placeholder="moonjibs"
                                                    value={filmerSearchText}
                                                    onChange={(e) => handleFilmerSearch(e.target.value)}
                                                />
                                                {filmerDropdown && (
                                                    <Dropdown
                                                        list={filmerDropdownOptions}
                                                        addItem={addFilmerItem}
                                                        accessProperty={true}
                                                        accessPropertyString={'username'}
                                                    />
                                                )}
                                            </div>
                                        </> : null
                                }
                            </div>
                            <div className="flex flex-col mb-3">
                                <div className="flex flex-row justify-between items-center mb-2">
                                    <label className="block text-xl font-medium">Edit Info</label>
                                    <IDXButtonIcon size={"text-2xl"} icon={chevronDownOutline}
                                        onClick={toggleClipInfo} />
                                </div>
                                {
                                    displayClipInfoEntry ?
                                        <>
                                            <div className="mb-6 ml-2">
                                                <label className="block mb-2 text-lg font-medium">Sport</label>
                                                <div className="relative w-full lg:max-w-sm">
                                                    <select
                                                        value={sport}
                                                        onChange={(event) => {
                                                            setSport(event.target.value);
                                                        }}
                                                        className="w-full p-2.5 text-gray-500 bg-white border rounded-md shadow-sm outline-none appearance-none focus:border-indigo-600"
                                                    >
                                                        <option selected hidden>Select a sport</option>
                                                        {sportOptions.map((sport) => (
                                                            <option id={sport.id.toString()}>{sport.name}</option>))}
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="mb-6 ml-2 location-container" ref={closeDropdown}>
                                                <label className="block mb-2 text-lg font-medium">
                                                    Location
                                                    <IonIcon icon={informationCircleOutline} color="theme-alternative"
                                                        className="ml-2"
                                                        onClick={() => presentToast("If you want your clip location private, don't enter one, we do not sell your data :)")}>
                                                    </IonIcon>
                                                </label>
                                                <input
                                                    className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ion-text-area-upload-clip"
                                                    placeholder="Optional since you could have filmed all over"
                                                    value={location}
                                                    onChange={(e) => setTrickLocation(e.target.value)}
                                                />
                                                {locationDropdown ?
                                                    <Dropdown list={locationDropdownOptions} addItem={addLocationItem}
                                                        accessProperty={true}
                                                        accessPropertyString={'description'}></Dropdown> : null}
                                            </div>
                                            <div className="ml-2 mb-3">
                                                <label className="block mb-2 text-lg font-medium">Date</label>
                                                <IonDatetimeButton className="justify-start" datetime="datetime" />
                                                <IonModal keepContentsMounted={true}>
                                                    <IonDatetime id="datetime" presentation="date" color="dark"
                                                        value={filmDate} max={currentDate}
                                                        onIonChange={(event) => setFilmDate(event.target.value.toString())} />
                                                </IonModal>
                                                <p id="helper-text-explanation"
                                                    className="mt-3 text-sm text-gray-500">If this edit took place
                                                    overtime, just select today's date.</p>
                                            </div>
                                            <div className="mb-6 ml-2">
                                                <IonCheckbox labelPlacement="start"
                                                    onIonChange={(ev) => onSetUnlisted(ev.detail.checked)}
                                                    className="text-lg">
                                                    {FOLLOWERS_ONLY}
                                                </IonCheckbox>
                                            </div>
                                        </> : null
                                }
                            </div>
                            <div>
                                <div className="flex flex-row justify-between mb-10">
                                    <IonButton className="neon-button" onClick={() => {
                                        setShowTrickForm(false);
                                        setDisplayTrickInfoEntry(false);
                                        setDisplayClipInfoEntry(false);
                                        setDisplayFilmerInfoEntry(false);
                                    }}>Back</IonButton>
                                    <IonButton className="neon-button-highlight" onClick={async () => {
                                        if (trickName && sport) {
                                            await Haptics.impact({ style: ImpactStyle.Light });
                                            setShowTagSelect(true);
                                        } else {
                                            presentToast("Please fill the edit name and sport fields to tag your edit!");
                                        }
                                    }}>Tag</IonButton>
                                    <IonButton className="neon-button-alt"
                                        onClick={handleUploadVideoFromFileOrLink}>Upload</IonButton>
                                </div>
                            </div>
                        </form>
                    </div>
                </IonContent>
            </IonPage>
        );
    } else {
        return (
            <IonPage>
                <CustomHelmet title={"Ecliptic // Upload"}
                    description={"Upload your clips to Ecliptic to build your bag of tricks and get noticed by the crew, sponsors, or the world"}
                    url={`https://ecliptic.day/upload`} />
                <IonHeader translucent={true}>
                    <IonToolbar>
                        <div>
                            <div className="flex min-h-12">
                                {options.map((option, index) => (
                                    <div
                                        key={index}
                                        className={`flex-1 py-3 px-4 text-center text-sm uppercase ${segmentValue === option.value ? 'text-white border-b-2 border-white font-bold' : 'text-[#9c9c9c]'}`}
                                        onClick={() => handleSegmentChange(option.value)}
                                    >
                                        {option.label}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </IonToolbar>
                </IonHeader>
                <IonContent>
                    {
                        segmentValue === ClipType.CLIP ?
                            <div className="flex flex-col text-center items-center justify-center">
                                <div className="text-2xl text-center items-center justify-center mt-10">
                                    Upload a clip/trick
                                </div>
                                <div className="flex justify-center items-center w-full mt-10">
                                    <label
                                        className="flex flex-col justify-center items-center w-10/12 h-64 rounded-lg border-2 border-dashed cursor-pointer"
                                        onClick={(e) => {
                                            // Use native file picker on Android to avoid slow base64 conversion
                                            if (platform === 'android') {
                                                e.preventDefault();
                                                pickVideoNative();
                                            } else if (gateWebUploadTap(hasVerifiedPhone, requirePhone, () => presentToast("Phone verified — tap upload again to choose your clip."))) {
                                                // iOS/web: a programmatic .click() on the hidden file input does NOT open the iOS
                                                // picker, so only intercept UNVERIFIED users (they re-tap after verifying). A
                                                // verified user falls through to the native <label>→<input> picker activation.
                                                e.preventDefault();
                                            }
                                        }}>
                                        <div className="flex flex-col justify-center items-center pt-5 pb-6">
                                            <svg aria-hidden="true" className="mb-3 w-10 h-10 text-gray-400" fill="none"
                                                stroke="currentColor" viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                                            </svg>
                                            <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">Click to upload</span>{isPlatform("mobile") || isPlatform("cordova") ? "" : " or drag and drop"}
                                            </p>
                                            {isPlatform("mobile") || isPlatform("cordova") ? "" :
                                                <p className="text-xs text-gray-500">.MP4, .MOV, .M4V</p>}
                                        </div>
                                        <input id="dropzone-file" type="file" accept='video/mp4,video/x-m4v,video/*'
                                            className="hidden" onChange={async (event) => {
                                                console.log(typeof (event))
                                                await Haptics.impact({ style: ImpactStyle.Light });
                                                onSelectFile(event)
                                            }} />
                                    </label>
                                </div>
                            </div>
                            :
                            segmentValue === ClipType.LINE ?
                                <div className="flex flex-col text-center items-center justify-center">
                                    <div className="text-2xl text-center items-center justify-center mt-10">
                                        Upload a line
                                    </div>
                                    <div className="flex justify-center items-center w-full mt-10">
                                        <label
                                            className="flex flex-col justify-center items-center w-10/12 h-64 rounded-lg border-2 border-dashed cursor-pointer"
                                            onClick={(e) => {
                                                if (platform === 'android') {
                                                    e.preventDefault();
                                                    pickVideoNative();
                                                } else if (gateWebUploadTap(hasVerifiedPhone, requirePhone, () => presentToast("Phone verified — tap upload again to choose your clip."))) {
                                                    // iOS/web: programmatic .click() won't open the iOS picker — only intercept
                                                    // UNVERIFIED users; a verified user gets the native label→input picker.
                                                    e.preventDefault();
                                                }
                                            }}>
                                            <div className="flex flex-col justify-center items-center pt-5 pb-6">
                                                <svg aria-hidden="true" className="mb-3 w-10 h-10 text-gray-400"
                                                    fill="none"
                                                    stroke="currentColor" viewBox="0 0 24 24"
                                                    xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                                                </svg>
                                                <p className="mb-2 text-sm text-gray-500"><span
                                                    className="font-semibold">Click to upload</span>{isPlatform("mobile") || isPlatform("cordova") ? "" : " or drag and drop"}
                                                </p>
                                                {isPlatform("mobile") || isPlatform("cordova") ? "" :
                                                    <p className="text-xs text-gray-500">.MP4, .MOV, .M4V</p>}
                                            </div>
                                            <input id="dropzone-file" type="file" accept='video/mp4,video/x-m4v,video/*'
                                                className="hidden" onChange={async (event) => {
                                                    console.log(typeof (event))
                                                    await Haptics.impact({ style: ImpactStyle.Light });
                                                    onSelectFile(event)
                                                }} />
                                        </label>
                                    </div>
                                </div>
                                : segmentValue === ClipType.EDIT ?
                                    <div className="flex flex-col text-center items-center justify-center">
                                        <div className="text-2xl text-center items-center justify-center mt-10">
                                            Upload an edit
                                        </div>
                                        <div className="mt-10 w-full">
                                            <IonAccordionGroup className="mx-5">
                                                <IonAccordion value="first">
                                                    <IonItem slot="header" color="favorite">
                                                        <IonLabel>Upload A File</IonLabel>
                                                    </IonItem>
                                                    <div className="ion-padding" slot="content">
                                                        <div className="flex justify-center items-center w-full">
                                                            <label
                                                                className="flex flex-col justify-center items-center w-10/12 h-64 rounded-lg border-2 border-dashed cursor-pointer"
                                                                onClick={(e) => {
                                                                    if (platform === 'android') {
                                                                        e.preventDefault();
                                                                        pickVideoNative();
                                                                    } else if (gateWebUploadTap(hasVerifiedPhone, requirePhone, () => presentToast("Phone verified — tap upload again to choose your clip."))) {
                                                                        // iOS/web: programmatic .click() won't open the iOS picker — only
                                                                        // intercept UNVERIFIED users; verified users get the native picker.
                                                                        e.preventDefault();
                                                                    }
                                                                }}>
                                                                <div
                                                                    className="flex flex-col justify-center items-center pt-5 pb-6">
                                                                    <svg aria-hidden="true"
                                                                        className="mb-3 w-10 h-10 text-gray-400"
                                                                        fill="none"
                                                                        stroke="currentColor" viewBox="0 0 24 24"
                                                                        xmlns="http://www.w3.org/2000/svg">
                                                                        <path strokeLinecap="round" strokeLinejoin="round"
                                                                            strokeWidth="2"
                                                                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                                                                    </svg>
                                                                    <p className="mb-2 text-sm text-gray-500"><span
                                                                        className="font-semibold">Click to upload</span>{isPlatform("mobile") || isPlatform("cordova") ? "" : " or drag and drop"}
                                                                    </p>
                                                                    {isPlatform("mobile") || isPlatform("cordova") ? "" :
                                                                        <p className="text-xs text-gray-500">.MP4, .MOV,
                                                                            .M4V</p>}
                                                                </div>
                                                                <input id="dropzone-file" type="file"
                                                                    accept='video/mp4,video/x-m4v,video/*'
                                                                    className="hidden"
                                                                    onChange={async (event) => {
                                                                        console.log(typeof (event))
                                                                        await Haptics.impact({ style: ImpactStyle.Light });
                                                                        onSelectFile(event)
                                                                    }} />
                                                            </label>
                                                        </div>
                                                    </div>
                                                </IonAccordion>
                                                <IonAccordion value="second">
                                                    <IonItem slot="header" color="favorite-secondary">
                                                        <IonLabel>Upload a Link</IonLabel>
                                                    </IonItem>
                                                    <div className="ion-padding" slot="content">
                                                        <IonItem>
                                                            <IonInput label="YouTube Link" labelPlacement="stacked"
                                                                placeholder="ex: https://www.youtube.com/watch?v=LNu_YZss2pw"
                                                                clearInput={true}
                                                                onIonInput={e => analyzeEditLink(e.detail.value)}></IonInput>
                                                        </IonItem>
                                                        <div className="flex justify-center items-center mt-4">
                                                            <IonButton className="ml-4 neon-button-alt"
                                                                onClick={() => goToVideoPreviewForEdit()}
                                                                disabled={!isValidLink}>Next</IonButton>
                                                        </div>
                                                    </div>
                                                </IonAccordion>
                                            </IonAccordionGroup>
                                        </div>
                                    </div>
                                    : null
                    }
                </IonContent>
            </IonPage >
        );
    }
};

export default Upload;
